// src/services/aeo/clientDocsWrite.ts
//
// Phase 2 documents: Oura writes the client's Google Docs as oura@, and a
// named person does every client-facing step. The one module screens import
// for the writes (types, calls, the switch). Reads stay in clientDocs.ts, and
// every write here tells its screens through the same `subscribeDocs`, so a
// Phase 1 board or drawer reads again after it.
//
// Public API                                           live route (handoff)
//   DOCS_WRITE_ON                                      every write control reads it (switch or dev sample)
//   isDocsWriteLive                                    both VITE_ENABLE_OURA_DOCS_WRITE_LIVE and _DOCS_LIVE are "true"
//   getDocsWriteStatus()                               GET    /documents/connection (.write)                         B11
//   getDocTemplates()                                  GET    /documents/templates                                   B12
//   getCreateTargets(businessId)                       GET    /businesses/{id}/docs-folder/targets                   B12
//   createDocument(businessId, body)                   POST   /businesses/{id}/documents                             B12
//   getSkillLibrary()                                  GET    /oura/skills                                           B13
//   addQuestion(docId, body)                           POST   /documents/{id}/questions                              B15
//   updateQuestion(docId, questionId, patch)           PATCH  /documents/{id}/questions/{qid}                        B15
//   deleteQuestion(docId, questionId)                  DELETE /documents/{id}/questions/{qid}                        B15
//   sendQuestions(docId)                               POST   /documents/{id}/questions/send                         B15
//   setDocLines(docId, body)                           PATCH  /documents/{id}/lines                                  B16
//   renameDocument(docId, body)                        POST   /documents/{id}/rename                                 B16
//   getShareCheck(docId)                               GET    /documents/{id}/share-check                            B17
//   shareDocument(docId, emails)                       POST   /documents/{id}/share                                  B17
//   fixShareCheck(docId, code)                         POST   /documents/{id}/share-check/fix                        B17 (lane 3)
//   getDocMessageDraft(businessId, kind, docIds)       GET    /businesses/{id}/documents/message-draft               B18
//   sendDocMessage(businessId, body)                   POST   /businesses/{id}/onboarding/messages                   B18
//   listDocMessages(businessId)                        GET    /businesses/{id}/documents/messages                    B18
//   replyInThread(docId, threadId, text, {key}?)       POST   /documents/{id}/threads/{tid}/reply                    B20
//   resolveThread(docId, threadId, body)               POST   /documents/{id}/threads/{tid}/resolve                  B20
//   reopenThread(docId, threadId, reason?)             POST   /documents/{id}/threads/{tid}/reopen                   B20
//   resolveAgreed(docId, body)                         POST   /documents/{id}/resolve-agreed                         B20
//   editOwnPost(docId, threadId, postId, text)         PATCH  /documents/{id}/threads/{tid}/posts/{pid}              B21
//   deleteOwnPost(docId, threadId, postId)             DELETE /documents/{id}/threads/{tid}/posts/{pid}              B21
//   cleanUpPlaceholders(businessId, body)              POST   /businesses/{id}/documents/placeholders/clean-up       B21
//   logAnswer(docId, threadId, body)                   POST   /documents/{id}/threads/{tid}/log                      B22
//   getApprovalRecord(docId)                           GET    /documents/{id}/approval-record                        B23
//   fileApprovedPdf(docId)                             POST   /documents/{id}/file-approved                          B24
//   listApprovedReports(businessId)                    GET    /businesses/{id}/documents/approved-reports            B24
//   getDocSummary(docId)                               GET    /documents/{id}/summary                                B25
//   makeDocSummary(docId)                              POST   /documents/{id}/summary                                B25
//   getClientEmail(docId)                              GET    /documents/{id}/client-email                           #6700
//   draftClientEmail(docId, options)                   POST   /documents/{id}/client-email/draft                     #6700
//   previewClientEmail(docId, body)                    POST   /documents/{id}/client-email/preview                   #6700
//   sendClientEmail(docId, body)                       POST   /documents/{id}/client-email/send                      #6700
//   sendClientEmailTest(docId, body)                   POST   /documents/{id}/client-email/test                      #6710
//   uploadClientFile(file)                             POST   /businesses/mine/documents/files (merchant)            B19
//   answerQuestion(docId, threadId, body)              POST   /businesses/mine/documents/{id}/threads/{tid}/answer   B19
//
// Every call resolves and never throws: {ok: true, data} or a DocsFailure
// (clientDocsTypes.ts) whose `code` is a write refusal slug from errors[0]
// (DocsWriteErrorCode). With the switch unset, the dev server serves the
// sample store (clientDocsWriteMock.ts); a build answers `notServed` on every
// call, and DOCS_WRITE_ON is false there, so no screen offers a write.

import axios, { type AxiosResponse } from "axios";
import { getCookie } from "@util/cookie";
import type { DocsResult, DocumentDetail, MerchantDocument } from "./clientDocsTypes";
import type {
    ApprovalRecord,
    ApprovedReport,
    ClientAnswerIn,
    ClientEmailDraft,
    ClientEmailPreview,
    ClientEmailSaved,
    MultiClientEmailDraft,
    ClientEmailPurpose,
    ClientEmailSendIn,
    ClientEmailState,
    ClientFile,
    CreateDocIn,
    CreateDocResult,
    CreateTarget,
    DocLinesIn,
    DocMessageDraft,
    DocMessageIn,
    DocMessageKind,
    DocMessagePerson,
    DocSummary,
    DocTemplate,
    DocsWriteStatus,
    FileApprovedResult,
    LogAnswerIn,
    RerunFillResult,
    PlaceholderCleanUp,
    PlaceholderCleanUpIn,
    QuestionIn,
    QuestionPatchIn,
    RenameIn,
    ResolveAgreedIn,
    ResolveIn,
    SentDocMessage,
    ShareCheck,
    ShareFixCode,
    SkillLibrary,
    SkillRun,
} from "./clientDocsWriteTypes";
import { announceDocsChanged, docsFailure, readDocsError } from "./clientDocs";
import { docsWriteMock } from "./clientDocsWriteMock";
import { DOCS_WRITE_LIVE, DOCS_WRITE_ON, DOCS_WRITE_SAMPLE_SERVES } from "../../oura/docs/writeFlag";

export * from "./clientDocsWriteTypes";
export { docNameOf, lineTwoOf, nextVersion, readsAsAgreement, versionLineOf } from "./clientDocsRules";

/** The write routes are live: VITE_ENABLE_OURA_DOCS_WRITE_LIVE and VITE_ENABLE_OURA_DOCS_LIVE are both exactly "true". */
export const isDocsWriteLive = DOCS_WRITE_LIVE;

/** The write sample store answers: the dev server, with Oura and the docs on fixtures. Never a build. */
export { DOCS_WRITE_ON, DOCS_WRITE_SAMPLE_SERVES };

/** The sample's answer, or "not available yet" anywhere else. */
function sample<T>(run: () => Promise<DocsResult<T>>): Promise<DocsResult<T>> {
    return DOCS_WRITE_SAMPLE_SERVES ? run() : Promise.resolve(docsFailure("notServed"));
}

const AEO_BASE = `${import.meta.env.VITE_TALENT_AI_BE_BASE_URL}/api/v1`;
const enc = encodeURIComponent;

function authHeaders(): Record<string, string> {
    const token = getCookie("authToken");
    return token ? { Authorization: `Bearer ${token}` } : {};
}

const opts = () => ({ headers: authHeaders(), timeout: 30_000 });

async function call<T>(what: string, run: () => Promise<AxiosResponse>): Promise<DocsResult<T>> {
    try {
        const res = await run();
        return { ok: true, data: (res.data as { data: T }).data };
    } catch (err) {
        return readDocsError(err, what);
    }
}

/** Tell every documents screen after a live write that succeeded (the sample store tells them itself). */
function thenEmit<T>(p: Promise<DocsResult<T>>): Promise<DocsResult<T>> {
    return p.then((r) => {
        if (r.ok) announceDocsChanged();
        return r;
    });
}

/** A read: live, or the sample. */
function readRoute<T>(what: string, live: () => Promise<AxiosResponse>, fake: () => Promise<DocsResult<T>>): Promise<DocsResult<T>> {
    return isDocsWriteLive ? call<T>(what, live) : sample(fake);
}

/** A write: live (then the broadcast), or the sample (which broadcasts on its own). */
function writeRoute<T>(what: string, live: () => Promise<AxiosResponse>, fake: () => Promise<DocsResult<T>>): Promise<DocsResult<T>> {
    return isDocsWriteLive ? thenEmit(call<T>(what, live)) : sample(fake);
}

const doc = (id: string) => `${AEO_BASE}/documents/${enc(id)}`;
const biz = (id: string) => `${AEO_BASE}/businesses/${enc(id)}`;
const thread = (docId: string, threadId: string) => `${doc(docId)}/threads/${enc(threadId)}`;

/* ------------------------------------------------------------------------- *
 * B11: write access
 * ------------------------------------------------------------------------- */

/** Route 1's `write` line; null = the server does not send it yet ("Read only"). */
export function getDocsWriteStatus(): Promise<DocsResult<DocsWriteStatus | null>> {
    if (!isDocsWriteLive) return sample(() => docsWriteMock.getWriteStatus());
    return call<{ write?: DocsWriteStatus | null }>("write status", () => axios.get(`${AEO_BASE}/documents/connection`, opts())).then((r) =>
        r.ok ? { ok: true as const, data: r.data.write ?? null } : r,
    );
}

/* ------------------------------------------------------------------------- *
 * B12 and B13: create a doc, the Skill library
 * ------------------------------------------------------------------------- */

export function getDocTemplates(): Promise<DocsResult<DocTemplate[]>> {
    // `custom=true`: the Custom row too (ourteam-backend#6516). Never sent by default, so a sheet from before it never offers one.
    return readRoute("templates", () => axios.get(`${AEO_BASE}/documents/templates`, { ...opts(), params: { custom: true } }), () => docsWriteMock.getTemplates());
}

/** Where a doc can be created; a folder shared with the client is listed and refused. */
export function getCreateTargets(businessId: string): Promise<DocsResult<CreateTarget[]>> {
    return readRoute("targets", () => axios.get(`${biz(businessId)}/docs-folder/targets`, opts()), () => docsWriteMock.getTargets(businessId));
}

/**
 * Create, link or convert a doc. It is never shared on create. Refusals:
 * folder_shared_with_client, doc_exists_unshared (`existingId`),
 * doc_type_unknown, template_missing, skill_not_added, skill_cap_reached,
 * link_not_a_doc, file_not_word, unknown_code, outlet_invalid; with `customType`
 * (ourteam-backend#6516): custom_type_invalid, code_not_in_plan, kind_not_for_custom.
 */
export function createDocument(businessId: string, body: CreateDocIn): Promise<DocsResult<CreateDocResult>> {
    return writeRoute("create", () => axios.post(`${biz(businessId)}/documents`, body, opts()), () => docsWriteMock.createDocument(businessId, body));
}

/**
 * Fill the SAME doc from its template again, after a fill that failed or never
 * finished (#4185). The server answers at once with the doc and `filling: true`
 * and does the 45 to 120 second fill in the background. Refusals (its own
 * sentence in `message`): fill_not_started, fill_in_progress, fill_done,
 * shared_with_client, fill_queue_full, fill_state_unavailable.
 */
export function rerunFill(documentId: string): Promise<DocsResult<RerunFillResult>> {
    return writeRoute("fill again", () => axios.post(`${doc(documentId)}/fill`, {}, opts()), () => docsWriteMock.rerunFill(documentId));
}

/**
 * The Skill library: 1 slot per doc type, each with the last 5 runs for that
 * type (`runs`, newest first), and the spend cap.
 */
export function getSkillLibrary(): Promise<DocsResult<SkillLibrary>> {
    return readRoute("skills", () => axios.get(`${AEO_BASE}/oura/skills`, opts()), () => docsWriteMock.getSkillLibrary());
}

/**
 * Run a doc's skill again, with an optional note (at most 500 characters), into
 * the same doc (ourteam-backend#6803: no versions): its new draft replaces the
 * old one when the run ends, and a failed run leaves it. Answers the `SkillRun`,
 * whose `documentId` is the doc. Refusals (the server's own sentence in
 * `message`): skill_not_added 409, skill_runner_not_configured 424,
 * skill_cap_reached 429, note_too_long 400, doc_shared_with_client 409,
 * folder_shared_with_client 409, skill_run_in_progress 409 (a run still writing the doc).
 */
export function rerunSkill(documentId: string, note: string | null): Promise<DocsResult<SkillRun>> {
    const trimmed = note?.trim() ?? "";
    return writeRoute(
        "skill re-run",
        () => axios.post(`${doc(documentId)}/skill-runs`, trimmed ? { note: trimmed } : {}, opts()),
        () => docsWriteMock.rerunSkill(documentId),
    );
}

/* ------------------------------------------------------------------------- *
 * B15 and B16: questions, Oura's lines, Rename
 * ------------------------------------------------------------------------- */

export function addQuestion(documentId: string, body: QuestionIn): Promise<DocsResult<DocumentDetail>> {
    return writeRoute("add question", () => axios.post(`${doc(documentId)}/questions`, body, opts()), () => docsWriteMock.addQuestion(documentId, body));
}

/** Edit a question; a sent one is edited in the doc too. */
export function updateQuestion(documentId: string, questionId: string, patch: QuestionPatchIn): Promise<DocsResult<DocumentDetail>> {
    return writeRoute(
        "edit question",
        () => axios.patch(`${doc(documentId)}/questions/${enc(questionId)}`, patch, opts()),
        () => docsWriteMock.updateQuestion(documentId, questionId, patch),
    );
}

/** Delete a draft, or a sent question no one has answered (refused `question_answered` otherwise). */
export function deleteQuestion(documentId: string, questionId: string): Promise<DocsResult<DocumentDetail>> {
    return writeRoute(
        "delete question",
        () => axios.delete(`${doc(documentId)}/questions/${enc(questionId)}`, opts()),
        () => docsWriteMock.deleteQuestion(documentId, questionId),
    );
}

/** Send to the doc: the numbered list in the body and 1 comment per question. Refusal: questions_none_to_send. */
export function sendQuestions(documentId: string): Promise<DocsResult<DocumentDetail>> {
    return writeRoute("send questions", () => axios.post(`${doc(documentId)}/questions/send`, {}, opts()), () => docsWriteMock.sendQuestions(documentId));
}

/** Oura's own lines only: the title line and the version line. */
export function setDocLines(documentId: string, body: DocLinesIn): Promise<DocsResult<DocumentDetail>> {
    return writeRoute("lines", () => axios.patch(`${doc(documentId)}/lines`, body, opts()), () => docsWriteMock.setLines(documentId, body));
}

/** Rename to the decided format (no `name`), or to a name already in it. Refusal: name_format_invalid. */
export function renameDocument(documentId: string, body: RenameIn = {}): Promise<DocsResult<DocumentDetail>> {
    return writeRoute("rename", () => axios.post(`${doc(documentId)}/rename`, body, opts()), () => docsWriteMock.rename(documentId, body));
}

/* ------------------------------------------------------------------------- *
 * B17 and B18: share, review email, chaser
 * ------------------------------------------------------------------------- */

export function getShareCheck(documentId: string): Promise<DocsResult<ShareCheck>> {
    return readRoute("share check", () => axios.get(`${doc(documentId)}/share-check`, opts()), () => docsWriteMock.shareCheck(documentId));
}

/**
 * Share comment-only to these addresses; the client hears in the bell only. Refusals: share_check_failed, share_emails_required, email_invalid.
 * `alreadySent` (backend #6553): the client received the doc before Oura, so no bell, dated the day it was marked Done.
 * Only an explicit true is sent; anything else posts `{emails}` exactly as before.
 */
export function shareDocument(documentId: string, emails: string[], how?: { alreadySent?: boolean }): Promise<DocsResult<DocumentDetail>> {
    const body = how?.alreadySent === true ? { emails, alreadySent: true } : { emails };
    return writeRoute("share", () => axios.post(`${doc(documentId)}/share`, body, opts()), () => docsWriteMock.share(documentId, emails, how?.alreadySent === true));
}

/**
 * Fix one pre-share item on a person's click: turn link sharing off, or make
 * every outside editor a commenter. Answers the check as it stands after.
 * Our internal notes are never deleted by Oura: a person deletes them in the doc.
 */
export function fixShareCheck(documentId: string, code: ShareFixCode): Promise<DocsResult<ShareCheck>> {
    return writeRoute("share fix", () => axios.post(`${doc(documentId)}/share-check/fix`, { code }, opts()), () => docsWriteMock.fixShare(documentId, code));
}

/** The server's ready draft of a review email or a chaser for these shared docs. Refusal: not_shared. */
export function getDocMessageDraft(businessId: string, kind: DocMessageKind, documentIds: string[]): Promise<DocsResult<DocMessageDraft>> {
    return readRoute(
        "message draft",
        () => axios.get(`${biz(businessId)}/documents/message-draft`, { ...opts(), params: { kind, documentIds: documentIds.join(",") } }),
        () => docsWriteMock.messageDraft(businessId, kind, documentIds),
    );
}

/**
 * Send a review email or a chaser through onboarding's send route, from
 * Angel at oura@ with a BCC to oura@. The named person who clicked is the
 * sender of record. Refusals: not_shared, share_emails_required,
 * email_invalid, message_empty.
 */
export function sendDocMessage(businessId: string, body: DocMessageIn): Promise<DocsResult<SentDocMessage>> {
    return writeRoute("send message", () => axios.post(`${biz(businessId)}/onboarding/messages`, body, opts()), () => docsWriteMock.sendMessage(businessId, body));
}

/** The review emails and chasers sent for this client, newest first. */
export function listDocMessages(businessId: string): Promise<DocsResult<SentDocMessage[]>> {
    return readRoute("messages", () => axios.get(`${biz(businessId)}/documents/messages`, opts()), () => docsWriteMock.listMessages(businessId));
}

/* ------------------------------------------------------------------------- *
 * B20 to B22: reply, resolve, reopen, our own posts, logged answers
 * ------------------------------------------------------------------------- */

/** Reply from Oura as oura@, signed with your name. Refusal: reply_required. */
/**
 * Reply in a thread. `idempotencyKey` goes as the `Idempotency-Key` header:
 * a retry with the same key should get the first post back, not a second one.
 * Harmless until the reply route (B20) honours it; until then a lost answer
 * is still the caller's to treat as "may have posted".
 */
export function replyInThread(documentId: string, threadId: string, text: string, options: { idempotencyKey?: string } = {}): Promise<DocsResult<DocumentDetail>> {
    const o = opts();
    const headers = options.idempotencyKey ? { ...o.headers, "Idempotency-Key": options.idempotencyKey } : o.headers;
    return writeRoute("reply", () => axios.post(`${thread(documentId, threadId)}/reply`, { text }, { ...o, headers }), () => docsWriteMock.reply(documentId, threadId, text));
}

/**
 * Resolve. When the client's last word is not an agreement the first call
 * is refused `client_not_agreed`; ask again, then send `confirmNotAgreed`.
 */
export function resolveThread(documentId: string, threadId: string, body: ResolveIn = {}): Promise<DocsResult<DocumentDetail>> {
    return writeRoute("resolve", () => axios.post(`${thread(documentId, threadId)}/resolve`, body, opts()), () => docsWriteMock.resolve(documentId, threadId, body));
}

export function reopenThread(documentId: string, threadId: string, reason?: string): Promise<DocsResult<DocumentDetail>> {
    return writeRoute(
        "reopen",
        () => axios.post(`${thread(documentId, threadId)}/reopen`, reason ? { reason } : {}, opts()),
        () => docsWriteMock.reopen(documentId, threadId, reason),
    );
}

/** Resolve every open question the client agreed to (or only `threadIds`). */
export function resolveAgreed(documentId: string, body: ResolveAgreedIn = {}): Promise<DocsResult<DocumentDetail>> {
    return writeRoute("resolve agreed", () => axios.post(`${doc(documentId)}/resolve-agreed`, body, opts()), () => docsWriteMock.resolveAgreed(documentId, body));
}

/** Edit a post Oura wrote (`{threadId}:open` for the opening comment). Refusal: not_our_post. */
export function editOwnPost(documentId: string, threadId: string, postId: string, text: string): Promise<DocsResult<DocumentDetail>> {
    return writeRoute(
        "edit post",
        () => axios.patch(`${thread(documentId, threadId)}/posts/${enc(postId)}`, { text }, opts()),
        () => docsWriteMock.editPost(documentId, threadId, postId, text),
    );
}

/** Delete a post Oura wrote. Refusals: not_our_post, question_answered. */
export function deleteOwnPost(documentId: string, threadId: string, postId: string): Promise<DocsResult<DocumentDetail>> {
    return writeRoute(
        "delete post",
        () => axios.delete(`${thread(documentId, threadId)}/posts/${enc(postId)}`, opts()),
        () => docsWriteMock.deletePost(documentId, threadId, postId),
    );
}

/** One Word file in the bulk convert plan (ourteam-backend#6088): what it becomes, or why it needs a manual convert. */
export interface ConvertWordItem {
    documentId: string;
    name: string;
    driveFileId: string;
    docType: string | null;
    /** `docType` is a custom doc type's name, read from the file's name (ourteam-backend#6519). */
    custom?: boolean;
    codes: string[];
    outlet: string | null;
    folderId: string | null;
    ready: boolean;
    reason: string | null;
}

export interface ConvertWordPlan {
    items: ConvertWordItem[];
    ready: number;
    maxPerCall: number;
}

export interface ConvertWordResult {
    documentId: string;
    ok: boolean;
    error?: string;
    message?: string;
}

export interface ConvertWordRun {
    results: ConvertWordResult[];
    converted: number;
    refused: number;
    notAttempted: number;
}

/** Every unconverted Word file in the client's folder and what it would become. No Drive call. */
export function getConvertWordPlan(businessId: string): Promise<DocsResult<ConvertWordPlan>> {
    return readRoute(
        "convert plan",
        () => axios.get(`${biz(businessId)}/documents/convert-word`, opts()),
        () => Promise.resolve({ ok: true as const, data: { items: [], ready: 0, maxPerCall: 20 } }),
    );
}

/** Convert these Word files, each through Create's convert path. A few per call: each one is a Drive copy. */
export function convertWordFiles(businessId: string, documentIds: string[]): Promise<DocsResult<ConvertWordRun>> {
    return writeRoute(
        "convert Word files",
        () => axios.post(`${biz(businessId)}/documents/convert-word`, { documentIds }, { headers: authHeaders(), timeout: 120_000 }),
        () => Promise.resolve({ ok: true as const, data: { results: [], converted: 0, refused: 0, notAttempted: 0 } }),
    );
}

/** The one-off clean-up of our old placeholder comments. Counts only unless `dryRun: false`. */
export function cleanUpPlaceholders(businessId: string, body: PlaceholderCleanUpIn = {}): Promise<DocsResult<PlaceholderCleanUp>> {
    return writeRoute(
        "placeholder clean-up",
        () => axios.post(`${biz(businessId)}/documents/placeholders/clean-up`, body, opts()),
        () => docsWriteMock.cleanUpPlaceholders(businessId, body),
    );
}

/** Log an answer the client gave by email, WhatsApp or a call: posted on their behalf. Refusals: answer_invalid, said_at_in_future. */
export function logAnswer(documentId: string, threadId: string, body: LogAnswerIn): Promise<DocsResult<DocumentDetail>> {
    return writeRoute("log answer", () => axios.post(`${thread(documentId, threadId)}/log`, body, opts()), () => docsWriteMock.logAnswer(documentId, threadId, body));
}

/* ------------------------------------------------------------------------- *
 * B23 to B25: approval record, the approved PDF, the AI summary
 * ------------------------------------------------------------------------- */

export function getApprovalRecord(documentId: string): Promise<DocsResult<ApprovalRecord>> {
    return readRoute("approval record", () => axios.get(`${doc(documentId)}/approval-record`, opts()), () => docsWriteMock.approvalRecord(documentId));
}

/**
 * File the PDF of the approved doc to the client's Reports; the doc stays as it
 * is (no next version since ourteam-backend#6803); the client hears in the bell only.
 * Refusals: not_approved, already_filed.
 */
export function fileApprovedPdf(documentId: string): Promise<DocsResult<FileApprovedResult>> {
    return writeRoute("file approved", () => axios.post(`${doc(documentId)}/file-approved`, {}, opts()), () => docsWriteMock.fileApproved(documentId));
}

export function listApprovedReports(businessId: string): Promise<DocsResult<ApprovedReport[]>> {
    return readRoute("approved reports", () => axios.get(`${biz(businessId)}/documents/approved-reports`, opts()), () => docsWriteMock.listReports(businessId));
}

/** The last AI summary of a doc, or null. Staff only. */
export function getDocSummary(documentId: string): Promise<DocsResult<DocSummary | null>> {
    return readRoute("summary", () => axios.get(`${doc(documentId)}/summary`, opts()), () => docsWriteMock.getSummary(documentId));
}

/**
 * Make one, only when staff open a doc that changed since the last. `fresh`: a
 * new one even when the doc is unchanged, at a model call's cost ("Make a new
 * summary", ourteam-backend#6045). Refusal: summary_cap_reached. It never sets a state.
 */
export function makeDocSummary(documentId: string, options: { fresh?: boolean } = {}): Promise<DocsResult<DocSummary>> {
    // A summary changes no document, so it does not tell the documents screens.
    if (!isDocsWriteLive) return sample(() => docsWriteMock.makeSummary(documentId));
    return call("make summary", () => axios.post(`${doc(documentId)}/summary`, options.fresh ? { fresh: true } : {}, opts()));
}

/* ------------------------------------------------------------------------- *
 * The client email for a finished doc (epic ourteam-backend#6697)
 * ------------------------------------------------------------------------- */

/** Whether the doc can have its client email (Done and shared), the draft kept for it, the last one sent. Never a model call. */
export function getClientEmail(documentId: string): Promise<DocsResult<ClientEmailState>> {
    return readRoute("client email", () => axios.get(`${doc(documentId)}/client-email`, opts()), () => docsWriteMock.clientEmail(documentId));
}

/**
 * Oura's draft: the one kept for this revision and purpose at no cost, or one
 * model call under the AI summaries' cap. `fresh` reads the doc again (a paid
 * call). Refusals: not_done, not_shared, not_client_doc, cap_reached,
 * model_unset, doc_unreadable, model_failed. Sends nothing, changes no doc.
 */
export function draftClientEmail(documentId: string, options: { purpose?: ClientEmailPurpose; fresh?: boolean } = {}): Promise<DocsResult<ClientEmailDraft>> {
    if (!isDocsWriteLive) return sample(() => docsWriteMock.draftClientEmail(documentId, options));
    // Reading the doc is a model call: give it longer than a plain read.
    return call("client email draft", () => axios.post(`${doc(documentId)}/client-email/draft`, options, { ...opts(), timeout: 120_000 }));
}

/** The email as staff changed it: the HTML the client would get, and its checks against who it goes to now. Never a model call. */
export function previewClientEmail(documentId: string, body: { subject: string; body: string; to: DocMessagePerson[] }): Promise<DocsResult<ClientEmailPreview>> {
    if (!isDocsWriteLive) return sample(() => docsWriteMock.previewClientEmail(documentId, body));
    return call("client email preview", () => axios.post(`${doc(documentId)}/client-email/preview`, body, opts()));
}

/* ------------------------------------------------------------------------- *
 * One client email for several docs (3 Nov 2026). Nothing staff type is kept.
 * ------------------------------------------------------------------------- */

const multi = (businessId: string, step: string) => `${AEO_BASE}/businesses/${encodeURIComponent(businessId)}/client-email/multi/${step}`;

/** Oura's email for these docs (up to 6, each shared with the client). A Google Doc's read is reused, or one paid call. */
export function draftMultiClientEmail(businessId: string, body: { documentIds: string[]; purpose?: ClientEmailPurpose | null }): Promise<DocsResult<MultiClientEmailDraft>> {
    return call("multi client email draft", () => axios.post(multi(businessId, "draft"), body, { ...opts(), timeout: 180_000 }));
}

/** The edited email as the client gets it. Never a model call. */
export function previewMultiClientEmail(businessId: string, body: { subject: string; body: string }): Promise<DocsResult<{ html: string }>> {
    return call("multi client email preview", () => axios.post(multi(businessId, "preview"), body, opts()));
}

/** Only to our own test inbox. Nothing is kept. */
export function sendMultiClientEmailTest(businessId: string, body: { subject: string; body: string }): Promise<DocsResult<{ to: string }>> {
    return call("multi client email test", () => axios.post(multi(businessId, "test"), body, opts()));
}

/** Send it as staff left it; one doc_delivery naming every doc. The same sendKey never sends twice. */
export function sendMultiClientEmail(
    businessId: string,
    body: {
        documentIds: string[];
        purpose: ClientEmailPurpose;
        to: DocMessagePerson[];
        cc: DocMessagePerson[];
        subject: string;
        body: string;
        sendKey: string;
        resendUnknown?: boolean;
    },
): Promise<DocsResult<SentDocMessage>> {
    return thenEmit(call("send multi client email", () => axios.post(multi(businessId, "send"), body, opts())));
}

/**
 * Keep the email as staff left it, for the doc, until it is sent or they go back to
 * Oura's draft: a refresh or a change in the Google Doc opens on it, and Oura does not
 * write it again. Never a model call; sends nothing. Answers `{saved}`.
 */
export function saveClientEmail(
    documentId: string,
    body: { purpose: ClientEmailPurpose; to: DocMessagePerson[]; cc: DocMessagePerson[]; subject: string; body: string },
): Promise<DocsResult<{ saved: ClientEmailSaved }>> {
    if (!isDocsWriteLive)
        return sample(async () => ({ ok: true as const, data: { saved: { ...body, html: "", checks: [], savedBy: null, savedAt: new Date().toISOString() } } }));
    return call("save client email", () => axios.put(`${doc(documentId)}/client-email/saved`, body, opts()));
}

/** Back to Oura's draft: the saved email goes. Answers `{saved: null}`. */
export function clearClientEmail(documentId: string): Promise<DocsResult<{ saved: null }>> {
    if (!isDocsWriteLive) return sample(async () => ({ ok: true as const, data: { saved: null } }));
    return call("clear client email", () => axios.delete(`${doc(documentId)}/client-email/saved`, opts()));
}

/**
 * Send it, from Angel at oura@ with a copy in the Oura inbox, kept as
 * `doc_delivery`. Refusals: check_failed, send_in_progress, send_unconfirmed,
 * share_emails_required (no_recipient), email_invalid (invalid_address), and the draft's.
 */
export function sendClientEmail(documentId: string, body: ClientEmailSendIn): Promise<DocsResult<SentDocMessage>> {
    return writeRoute("send client email", () => axios.post(`${doc(documentId)}/client-email/send`, body, opts()), () => docsWriteMock.sendClientEmail(documentId, body));
}

/**
 * A test of the email as it stands, sent only to our own test inbox (the server
 * picks it; ourteam-backend#6710). Nothing goes to the client and nothing is
 * kept. Refusals: the draft's, no_test_inbox.
 */
export function sendClientEmailTest(documentId: string, body: { subject: string; body: string }): Promise<DocsResult<{ to: string }>> {
    if (!isDocsWriteLive) return sample(() => docsWriteMock.testClientEmail(documentId));
    return call("client email test", () => axios.post(`${doc(documentId)}/client-email/test`, body, opts()));
}

/* ------------------------------------------------------------------------- *
 * B19: the client's side (merchant token)
 * ------------------------------------------------------------------------- */

/** A file the client attaches to an answer, stored in their Drive folder. */
export function uploadClientFile(file: File): Promise<DocsResult<ClientFile>> {
    if (!isDocsWriteLive) return sample(() => docsWriteMock.uploadClientFile(file.name));
    const form = new FormData();
    form.append("file", file);
    return call("upload file", () => axios.post(`${AEO_BASE}/businesses/mine/documents/files`, form, opts()));
}

/**
 * The client answers a question in Oura; Oura posts it into the doc thread on
 * their behalf. Agree resolves it (the approval of record); Change leaves it
 * with us. Never a pack item. Refusals: thread_resolved, note_required,
 * option_unknown, answer_invalid.
 */
export function answerQuestion(documentId: string, threadId: string, body: ClientAnswerIn): Promise<DocsResult<MerchantDocument>> {
    return writeRoute(
        "answer",
        () => axios.post(`${AEO_BASE}/businesses/mine/documents/${enc(documentId)}/threads/${enc(threadId)}/answer`, body, opts()),
        () => docsWriteMock.answer(documentId, threadId, body),
    );
}
